import { inject, Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { createEffect, Actions, ofType } from "@ngrx/effects";
import { EMPTY, Observable } from "rxjs";
import {
    withLatestFrom,
    mergeMap,
    map,
    catchError,
    tap,
    filter,
    switchMap,
    retry,
} from "rxjs/operators";
import { RootState } from "../root";
import { DocumentState, Signature } from "./state";
import { ConsultationService } from "../../services/consultation.service";
import { CreateDocumentResult, SignatureFieldMetadata } from "../../data/model";
import { EsignService } from "../../print/esign/esign.service";
import { PersistenceService } from "../model/persistence.service";
import { NotificationService } from "../../services/notification.service";
import * as Document from "./actions";
import { LoggerService } from "../../services/logger.service";

const signaturePrescale = 1.2;

@Injectable()
export class DocumentEffects {
    private logger = inject(LoggerService);

    constructor(
        private actions$: Actions,
        private store$: Store<RootState>,
        private persistence: PersistenceService,
        private consultationService: ConsultationService,
        private esignService: EsignService,
        private notificationService: NotificationService,
    ) {}

    buildEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(Document.Build.type),
                withLatestFrom(this.store$),
                mergeMap(([anyAction, state]) => {
                    const action = anyAction as Document.Build;
                    if (state.document.beingBuilt) {
                        this.logger.log("Document is already being built.");
                        return EMPTY;
                    }
                    if (!state.document.dirty && !action.force) {
                        this.logger.log(
                            "Document is still up-to-date, not rebuilt.",
                        );
                        return EMPTY;
                    }
                    this.logger.log("The document is rebuilt ...");
                    this.store$.dispatch(
                        Document.Update.create()
                            .set("document", null)
                            .set("documentUrl", null)
                            .set("metadata", null)
                            .set("signatures", [])
                            .set("beingBuilt", true)
                            .set("lastError", null),
                    );
                    return this.persistence.createDocument(
                        state.model.ConsultationProcess,
                    );
                }),
                withLatestFrom(this.store$),
                mergeMap(([x, state]) => {
                    const result: CreateDocumentResult = x as any;
                    const url = this.consultationService.getDocumentUrl(
                        result.Document.Id,
                    );
                    this.logger.log("Built a new PDF document: %s", url);
                    this.store$.dispatch(
                        Document.Update.create()
                            .set("documentUrl", url)
                            .set("metadata", result.Document)
                            .set(
                                "signatures",
                                this.prepareSignatures(result.SignatureFields),
                            )
                            .set("beingBuilt", false)
                            .set("lastError", null),
                    );
                    return this.consultationService.loadDocument(
                        result.Document,
                    );
                }),
                withLatestFrom(this.store$),
                tap(([blob, state]) => {
                    this.store$.dispatch(
                        Document.Update.create()
                            .set("document", blob as Blob)
                            .set("dirty", false),
                    );
                }),
                catchError((error, caught) => {
                    this.store$.dispatch(
                        Document.Update.create()
                            .set("beingBuilt", false)
                            .set("lastError", error.toString()),
                    );
                    this.notificationService.errorExt(
                        "Dokument erstellen",
                        "Das Dokument konnte nicht erstellt werden.",
                        error,
                    );
                    return caught;
                }),
            ),
        { dispatch: false },
    );

    prepareSignatures(metadata: SignatureFieldMetadata[]) {
        return metadata.map((field, index) => {
            const f = signaturePrescale;
            const width = (field.X2 - field.X1) * f;
            const height = (field.Y2 - field.Y1) * f;

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;

            return new Signature(
                index,
                field,
                field.X1 * f,
                field.Y1 * f,
                width,
                height,
                null,
            );
        });
    }

    finalizeEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(Document.Finalize.type),
                withLatestFrom(this.store$),
                mergeMap(([anyAction, state]) => {
                    const cp = state.model.ConsultationProcess;
                    if (!cp) {
                        this.logger.log("There is no CP to finalize.");
                        return EMPTY;
                    }
                    if (cp.IsFinished) {
                        this.logger.log("CP is already finalized.");
                        return EMPTY;
                    }
                    const signatures = this.esignService.getSignatures(
                        state.document,
                    );
                    if (signatures.length > 0) {
                        return this.consultationService.signAndFinalizeConsultationProcess(
                            cp,
                            signatures,
                        );
                    } else {
                        // document has no signature fields
                        return this.consultationService.finalizeConsultationProcess(
                            cp,
                        );
                    }
                }),
                withLatestFrom(this.store$),
                tap(([_, state]) => {
                    // TODO: Alternative: modify CP, ReplaceDatabase()
                    this.persistence.reload();
                }),
                retry(2),
                catchError((error, caught) => {
                    this.notificationService.errorExt(
                        "Dokumentation abschließen",
                        "Die Beratungsdokumentation konnte nicht abgeschlossen werden.",
                        error,
                    );
                    return caught;
                }),
            ),
        { dispatch: false },
    );
}
